import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';

export const LandingPage = () => {
  return (
    <div className="min-h-screen bg-obsidian" data-testid="landing-page">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <span className="font-heading font-bold text-xl text-white">SC</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-white tracking-tight">Solo Cracks</h1>
              <span className="text-xs text-slate-400 uppercase tracking-widest">Connect</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">
                Iniciar Sesión
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Registrar Club
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/3621104/pexels-photo-3621104.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&dpr=2')`,
          }}
        />
        {/* Overlay */}
        <div className="hero-overlay absolute inset-0" />

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="animate-fade-in">
            <span className="inline-block px-4 py-2 bg-indigo-600/20 border border-indigo-500/30 rounded-full text-indigo-300 text-sm font-medium mb-8">
              Solo Cracks Connect
            </span>
          </div>
          
          <h1 className="font-heading text-5xl md:text-7xl font-bold text-white tracking-tight leading-none animate-fade-in animate-delay-100">
            EL MERCADO DE FICHAJES
            <span className="block text-indigo-400">DEL FÚTBOL FEMENINO</span>
          </h1>
          
          <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed animate-fade-in animate-delay-200">
            Plataforma exclusiva de Solo Cracks para clubes de nuestra red. 
            Publica tus necesidades y jugadoras disponibles para agilizar las operaciones.
          </p>
          
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in animate-delay-300">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg font-heading uppercase tracking-wide" data-testid="hero-register-btn">
                Registrar Mi Club
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg font-heading uppercase tracking-wide" data-testid="hero-login-btn">
                Ya tengo cuenta
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-2.5 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 uppercase tracking-tight">
              Todo lo que necesitas en un solo lugar
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              Una plataforma diseñada para facilitar las operaciones entre clubes de nuestra red.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="stat-card" data-testid="feature-players">
              <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-indigo-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-800 uppercase">
                Jugadoras Disponibles
              </h3>
              <p className="mt-2 text-slate-600">
                Publica jugadoras en busca de nuevo club, ya sea por transferencia o préstamo.
              </p>
            </div>

            <div className="stat-card" data-testid="feature-needs">
              <div className="w-14 h-14 bg-lime-100 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-7 h-7 text-lime-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-800 uppercase">
                Necesidades de Clubes
              </h3>
              <p className="mt-2 text-slate-600">
                Indica qué posiciones buscas y los requisitos específicos para tu equipo.
              </p>
            </div>

            <div className="stat-card" data-testid="feature-network">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-amber-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-800 uppercase">
                Red Solo Cracks
              </h3>
              <p className="mt-2 text-slate-600">
                Acceso exclusivo para clubes de la red Solo Cracks. Gestionamos cada operación.
              </p>
            </div>

            <div className="stat-card" data-testid="feature-fast">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-7 h-7 text-red-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-800 uppercase">
                Proceso Ágil
              </h3>
              <p className="mt-2 text-slate-600">
                Solo Cracks recibe tus necesidades y te presenta las mejores opciones.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-obsidian">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
            ¿Listo para unirte a la red?
          </h2>
          <p className="mt-4 text-lg text-slate-400 max-w-xl mx-auto">
            Registra tu club y comienza a conectar con otros equipos de fútbol femenino.
          </p>
          <div className="mt-10">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-6 text-lg font-heading uppercase tracking-wide" data-testid="cta-register-btn">
                Registrar Club
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-500 text-sm">
            © 2024 Solo Cracks Connect. Plataforma interna para clubes de nuestra red.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
